import type { ReactNode } from 'react';
import type { FlowTone } from './flow-state';

/**
 * A place's state as a shape, so it reads without colour: an empty ring when idle, chevrons when moving
 * (three when busy), two bars when held, a clock when slow, a warning triangle when staff must act.
 */
const SHAPE: Record<FlowTone | 'done', ReactNode> = {
  idle: <circle cx="8" cy="8" r="4.5" />,
  moving: <path d="M4.5 4l4 4-4 4M9 4l4 4-4 4" />,
  busy: <path d="M2.5 4l3.5 4-3.5 4M6.5 4l3.5 4-3.5 4M10.5 4l3.5 4-3.5 4" />,
  held: <path d="M5.5 3.5v9M10.5 3.5v9" />,
  slow: (
    <>
      <circle cx="8" cy="8" r="5.5" />
      <path d="M8 5v3.2l2.2 1.5" />
    </>
  ),
  stuck: (
    <>
      <path d="M8 2.2l6.2 10.9H1.8z" />
      <path d="M8 6.4v3.2M8 11.3v.2" />
    </>
  ),
  done: (
    <>
      <circle cx="8" cy="8" r="5.8" />
      <path d="M5.4 8.2l1.9 1.9 3.5-3.9" />
    </>
  ),
};

const WORD: Record<FlowTone | 'done', string> = {
  idle: 'Idle',
  moving: 'Moving',
  busy: 'Busy',
  held: 'Waiting by design',
  slow: 'Slow',
  stuck: 'Needs staff',
  done: 'Done',
};

export function FlowGlyph({
  tone,
  size = 16,
}: {
  tone: FlowTone | 'done';
  size?: number;
}): ReactNode {
  return (
    <svg
      className="fl-glyph"
      viewBox="0 0 16 16"
      width={size}
      height={size}
      fill="none"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinecap="round"
      strokeLinejoin="round"
      role="img"
      aria-label={WORD[tone]}
    >
      {SHAPE[tone]}
    </svg>
  );
}

/** A quarter hour of pages a minute as a filled line; flat on the baseline when nothing moved. */
export function FlowSpark({ values, label }: { values: number[]; label: string }): ReactNode {
  const max = Math.max(1, ...values);
  const step = values.length > 1 ? 100 / (values.length - 1) : 100;
  const points = values.map(
    (v, i) => `${(i * step).toFixed(1)} ${(22 - (v / max) * 20).toFixed(1)}`,
  );
  const line = `M${points.join(' L')}`;
  const quiet = values.every((v) => v === 0);
  return (
    <svg
      className={quiet ? 'fl-spark fl-spark--quiet' : 'fl-spark'}
      viewBox="0 0 100 24"
      preserveAspectRatio="none"
      role="img"
      aria-label={label}
    >
      {quiet ? null : <path className="fl-spark__area" d={`${line} L100 24 L0 24 Z`} />}
      <path className="fl-spark__line" d={line} vectorEffect="non-scaling-stroke" />
    </svg>
  );
}
